export enum MessageType {
 AI_MESSAGE = "aiMessage",
 /** Transcript-only: an intermediate assistant round that produced tool calls.
  * Never rendered by the chat UI. */
 ASSISTANT_TOOL_CALLS = "assistantToolCalls",
 CHARACTER_PHOTOS_UPLOADED = "characterPhotosUploaded",
 ERROR_MESSAGE = "errorMessage",
 NEW_CONVERSATION = "newConversation",
 NOTIFICATION = "notification",
 PERFORMER_CONTRACT_DECLINED = "performerContractDeclined",
 SUPPORT_MESSAGE = "supportMessage",
 CANCEL = "cancel",
 SYSTEM_MESSAGE = "systemMessage",
 /** Transcript-only: an MCP tool result (a `role:"tool"` message in the rebuilt transcript).
  * Never rendered by the chat UI. */
 TOOL_MESSAGE = "toolMessage",
 /** Ephemeral typing-indicator ping — never persisted as a ChatMessage row. */
 TYPING = "typing",
 UPDATED_CONVERSATION = "updatedConversation",
 USER_MESSAGE = "userMessage",
}

/** Transcript-only message types: rows persisted for multi-turn bot memory that never
 * render as chat bubbles and never count as unread. Shared so the API's unread-count
 * whitelist and the web's render filter derive from one set and cannot drift.
 * (SYSTEM_MESSAGE renders in the UI but is deliberately excluded from unread counts —
 * that exclusion stays local to the count query, not shared here.) */
export const TRANSCRIPT_ONLY_MESSAGE_TYPES: readonly MessageType[] = [
 MessageType.ASSISTANT_TOOL_CALLS,
 MessageType.TOOL_MESSAGE,
] as const;

export enum ConversationStatus {
 OPEN = "open",
 CLOSED = "closed",
}

export enum ConversationType {
 SUPPORT = "support",
 TALENT_MANAGER = "talentManager",
 PROJECT = "project",
 BOT = "bot",
}

export enum ConversationParticipantRole {
 AGENCY = "agency",
 TALENT_MANAGER = "talentManager",
 SUPPORT = "support",
 ADMIN_OBSERVER = "adminObserver",
}

export enum MentionTargetType {
 USER = "user",
 BOT = "bot",
 SUPPORT = "support",
}

/** Ephemeral CANCEL WS frame (bot channels): cancels the in-flight bot turn for
 * `conversation_id`. Never persisted as a ChatMessage row. `message_id` is optional
 * precision — under the single-flight invariant (composer locked until the reply lands)
 * `conversation_id` alone identifies the turn. */
export type TChatCancelFrame = {
 conversation_id: string;
 message_id?: string;
};

export type TNotificationPreferences = {
 emailOnMention: boolean;
};

/** OpenAI-format tool call — mirrors the agent-router's `ToolCall` (conversation/llm-client.ts).
 * Stored in `ChatMessage.llm_payload` on `ASSISTANT_TOOL_CALLS` rows and sent as `tool_calls`
 * on assistant messages in the transcript forwarded to the agent-router. */
export type TOpenAiToolCall = {
 id: string;
 type: "function";
 function: { name: string; arguments: string };
};

/** `ChatMessage.llm_payload` column value: `tool_calls` on `ASSISTANT_TOOL_CALLS` rows,
 * `{ tool_call_id, name }` on `TOOL_MESSAGE` rows, absent on plain text rows. */
export type TChatMessageLlmPayload = {
 tool_calls?: TOpenAiToolCall[];
 tool_call_id?: string;
 name?: string;
};
